import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { EventInput } from '@fullcalendar/core';
import { CalendarSchedule } from '../_models/Monitoring/Schedules';
import { DateTime } from 'luxon';
import { DatePipe, formatDate } from '@angular/common';
import moment from 'moment-timezone';
import { DeviceDto } from '../_models/Devices/Device';
import { Site } from '../_models/Sites/site';

@Injectable({
  providedIn: 'root'
})
export class SchedulerService {

  fullCalendarEvents: Array<EventInput> = [];
  daysOfWeek: Array<string> = [];
  selectedDate = new Date();

  readonly nonDSTDate: string = "2019-12-01T12:00:00Z";

  constructor(@Inject(LOCALE_ID) private locale: string) { }

  //#region Full Calendar Functions
  /**
  * This function creates the full calendar event needed for full calendar
  * @param schedules This is the schedules parameter
  * @returns returns EventInput Model by FullCalendar
  */
  createFullcalendarEvents(schedules: CalendarSchedule[], resourceId?: string, isGuardTour = false): Array<EventInput> {
    this.fullCalendarEvents = [];
    // Display schedule templates for the past year
    const weeksToDisplay = 52;

    for (let schedule of schedules) {
      let tempSched: EventInput = {};

      if (schedule.recurrenceRule === '') {
        tempSched.start = schedule.startTime.toISOString();
        tempSched.end = schedule.endTime.toISOString();
        this.fullCalendarEvents.push(this.createEventObject(tempSched, schedule, resourceId, isGuardTour));
      }
      else {
        const endDate = DateTime.fromJSDate(schedule.endTime);
        const startDate = DateTime.fromJSDate(schedule.startTime);
        const duration = endDate.diff(startDate, ["years", "months", "days", "hours"]);

        // Current and future events
        tempSched = {
          duration: duration,
          rrule: {
            freq: 'weekly',
            dtstart: startDate.toISO(),
          }
        };
        this.fullCalendarEvents.push(this.createEventObject(tempSched, schedule, resourceId, isGuardTour));

        // Past events
        for (let i = 1; i <= weeksToDisplay; i++) {
          let pastWeekStart = startDate.minus({ weeks: i, days: 1 });
          let pastWeekEnd = pastWeekStart.plus({ days: 6 });

          let pastTempSched: EventInput = {
            rrule: {
              freq: 'weekly',
              dtstart: pastWeekStart.toISO(),
              until: pastWeekEnd.toISO()
            },
            duration: duration
          };
          this.fullCalendarEvents.push(this.createEventObject(pastTempSched, schedule, resourceId, isGuardTour));
        }
      }
    }

    return this.fullCalendarEvents;
  }

  private createEventObject(sched: EventInput, originalSchedule: CalendarSchedule, resourceId?: string, isGuardTour = false): EventInput {
    const removeText = originalSchedule.recurrenceRule === '' ? 'Click to Remove Monitoring Hours' : '';
    const singleChild = this.singleChildDisplay(originalSchedule, removeText, resourceId);
    const multipleChild = this.multipleChildDisplay(originalSchedule, removeText, resourceId);

    return {
      ...sched,
      groupId: originalSchedule.id.toString(),
      title: this.getEventTitle(originalSchedule, resourceId),
      color: originalSchedule.categoryColor,
      display: originalSchedule.recurrenceRule === '' && isGuardTour ? multipleChild : singleChild,
      resourceId: resourceId,
      editable: false
    };
  }

  private singleChildDisplay(schedule: CalendarSchedule, removeText: string, resourceId: string | undefined) {
    return `
      <div class="d-flex flex-column flex-row-fluid">
        <div class="d-flex align-items-center flex-wrap mb-0">
          <label href="#" class="text-gray-800 fw-bold me-6">${schedule.subject}</label>
        </div>
        <div class="d-flex align-items-center flex-wrap mb-0">
          <span class="text-gray-800 fs-7 fw-normal pt-1">Site Time (${schedule.zone}): ${schedule.localTimeString}</span>
          <span class="text-gray-800 fs-7 fw-normal pt-1">Manila Time: ${schedule.manilaTimeString}</span>
          <span class="text-gray-800 fs-7 fw-normal pt-1">Toronto Time: ${schedule.torontoTimeString}</span>
          <span class="text-gray-800 fs-7 fw-normal pt-1">Dallas Time: ${schedule.dallasTimeString}</span>
        </div>
        <div class="separator separator-solid mt-2 mb-2"></div>
        ${schedule.childEntities.length > 0 ? `
          <div class="d-flex align-items-center flex-wrap mb-0">
            ${resourceId !== undefined ? `<span class="text-gray-800 fs-7 fw-normal pt-1 mt-2 mb-2">${removeText}</span>` : ''}
            <label href="#" class="text-gray-800 fw-bold me-6">${this.getChildType(schedule.childType)}</label>
          </div>
          <div class="d-flex align-items-center flex-wrap mb-0">
            <span class="text-gray-800 fs-7 fw-normal pt-1">${schedule.childEntities.filter(Boolean).join('\n')}</span>
          </div>
        ` : ''}
        ${schedule.recurrenceRule === '' ? `<div class="separator separator-solid mt-2 mb-2"></div><div class="d-flex align-items-center flex-wrap mb-0"><label href="#" class="text-gray-800 fw-bold me-6"> Created By: </label></div>` : ''}
        ${schedule.recurrenceRule === '' ? `<div class="d-flex align-items-center flex-wrap mb-0"><span class="text-gray-800 fs-7 fw-normal pt-1">${schedule.createdByName ?? '--'}</span></div>` : ''}
      </div>
    `;
  }

  private multipleChildDisplay(schedule: CalendarSchedule, removeText: string, resourceId: string | undefined) {
    return `
      <div class="d-flex flex-column flex-row-fluid">
        <div class="d-flex align-items-center flex-wrap mb-0">
          <label href="#" class="text-gray-800 fw-bold me-6">${schedule.subject}</label>
        </div>
        <div class="d-flex align-items-center flex-wrap mb-0">
          <span class="text-gray-800 fs-7 fw-normal pt-1">Site Time (${schedule.zone}): ${schedule.localTimeString}</span>
          <span class="text-gray-800 fs-7 fw-normal pt-1">Manila Time: ${schedule.manilaTimeString}</span>
          <span class="text-gray-800 fs-7 fw-normal pt-1">Toronto Time: ${schedule.torontoTimeString}</span>
          <span class="text-gray-800 fs-7 fw-normal pt-1">Dallas Time: ${schedule.dallasTimeString}</span>
        </div>
        <div class="separator separator-solid mt-2 mb-2"></div>
        ${(schedule.nvrEntities?.length ?? 0) > 0 ? `
          <div class="d-flex align-items-center flex-wrap mb-0">
            ${resourceId !== undefined ? `<span class="text-gray-800 fs-7 fw-normal pt-1 mt-2 mb-2">${removeText}</span>` : ''}
            <label href="#" class="text-gray-800 fw-bold me-6">${this.getChildType('nvr')}</label>
          </div>
          <div class="d-flex align-items-center flex-wrap mb-0">
            <span class="text-gray-800 fs-7 fw-normal pt-1">${schedule.nvrEntities!.filter(Boolean).join('\n')}</span>
          </div>
        ` : ''}
        <div class="separator separator-solid mt-2 mb-2"></div>
        <div class="d-flex align-items-center flex-wrap mb-0">
          ${resourceId !== undefined ? `<span class="text-gray-800 fs-7 fw-normal pt-1 mt-2 mb-2">${removeText}</span>` : ''}
          <label href="#" class="text-gray-800 fw-bold me-6">${this.getChildType('camera')}</label>
        </div>
        <div class="d-flex align-items-center flex-wrap mb-0">
          <span class="text-gray-800 fs-7 fw-normal pt-1">${schedule.cameraEntities?.toString().split(',').join('\n')}</span>
        </div>
        ${schedule.recurrenceRule === '' ? `<div class="separator separator-solid mt-2 mb-2"></div><div class="d-flex align-items-center flex-wrap mb-0"><label href="#" class="text-gray-800 fw-bold me-6"> Created By: </label></div>` : ''}
        ${schedule.recurrenceRule === '' ? `<div class="d-flex align-items-center flex-wrap mb-0"><span class="text-gray-800 fs-7 fw-normal pt-1">${schedule.createdByName ?? '--'}</span></div>` : ''}
      </div>
    `;
  }

  getEventTitle(schedule: CalendarSchedule, resourceId?: string): string {
    let eventTitle = '<div class="d-flex flex-row justify-content-between flex-wrap py-3">' +
      '<div class="d-flex flex-wrap flex-lg-row flex-sm-column-reverse text-white fs-7 fw-normal ps-1">' +

      // Schedule Start Date/Time
      '<span class="me-3">' + this.convertDateToSimpleTime(schedule.startTime) + '</span>' +

      // Schedule Label
      '<label href="#" class="fw-bold order-1 order-lg-0">' + schedule.subject + '</label>' +
      '</div>' +

      // Schedule End Date/Time
      '<span class="text-white fs-7 fw-normal px-1">' + this.convertDateToSimpleTime(schedule.endTime) +
      '</span>' +
      ((resourceId !== undefined) ? '<span class="text-white fs-7 fw-normal pt-1 mt-2 mb-2"></span>' : '') +
      '</div>';
    return eventTitle
  }

  getChildType(childType: string): string {
    let type = '';
    if (childType == 'site') {
      type = 'Assigned to Site/s:'
    }
    else if (childType == 'nvr') {
      type = 'Assigned to NVR/s:'
    }
    else if (childType == 'camera') {
      type = 'Assigned to Camera/s:'
    }

    return type;
  }

  /**
 * This function converts the date to just time
 * @param date This is the date parameter
 * @returns returns the time only
 */
  convertDateToTime(date: Date): string {
    const time = date.toLocaleTimeString('en-US');
    return DateTime.fromFormat(time, 'tt').toFormat('TT');
  }

  convertDateToSimpleTime(date: Date): string {
    const time = DateTime.fromJSDate(date);
    return time.toFormat('ccc | t');
  }

  //#endregion Full Calendar Functions

  /**
   * This function gets the new schedule that has been filtered and is now unique
   * @param filter
   * @param calendarSchedules
   * @param cameraResourceDataSource
   * @param nvrResourceDataSource
   * @returns
   */
  getFilteredAndUniqueSchedules(filter: any, calendarSchedules: Array<Object>, cameraResourceDataSource: DeviceDto[], nvrResourceDataSource: DeviceDto[]): CalendarSchedule[] {
    let filteredSched = calendarSchedules.filter(function (schedule: any) {
      for (let key in filter) {
        if (schedule[key] === undefined || schedule[key] != filter[key]) {
          return false;
        }
      }
      return true;
    });

    //Get unique schedules
    let uniqueSched: Array<CalendarSchedule> = [];

    filteredSched.forEach(function (item: any) {
      let isPresent = uniqueSched.filter(function (elem: any) {
        if (item["subject"] != 'Monitored Override' && item["subject"] != 'Non-Monitored Override') {
          return elem.subject == item["subject"] && elem.recurrenceRule == item["recurrenceRule"] &&
            elem.startTime.toISOString() == item["startTime"].toISOString() &&
            elem.endTime.toISOString() == item["endTime"].toISOString() && elem.isAssigned == item["isAssigned"];
        }
        else {
          return elem.subject == item["subject"] &&
            elem.startTime.toISOString() == item["startTime"].toISOString() &&
            elem.endTime.toISOString() == item["endTime"].toISOString() && elem.isAssigned == item["isAssigned"];
        }
      });

      if (isPresent.length === 0) {
        let insertItem = Object.assign(new CalendarSchedule(), item);
        uniqueSched.push(insertItem);
      }
    });

    // List down child entities
    let childType = '';
    let idName = '';

    if (filter['nvrID'] !== undefined) {
      childType = 'camera';
      idName = 'cameraID';
    }
    else if (filter['siteID'] !== undefined) {
      childType = 'nvr';
      idName = 'nvrID';
    }
    else if (filter['customerID'] !== undefined) {
      childType = 'site';
      idName = 'siteID';
    }

    uniqueSched.forEach(item => {
      let matchedSchedules = filteredSched.filter(function (elem: any) {
        if (item.subject != 'Monitored Override' && item.subject != 'Non-Monitored Override') {
          return elem["subject"] == item.subject && elem["recurrenceRule"] == item.recurrenceRule &&
            elem["startTime"].toISOString() == item.startTime.toISOString() &&
            elem["endTime"].toISOString() == item.endTime.toISOString() && elem["isAssigned"] == item.isAssigned;
        }
        else {
          return elem["subject"] == item.subject &&
            elem["startTime"].toISOString() == item.startTime.toISOString() &&
            elem["endTime"].toISOString() == item.endTime.toISOString() && elem["isAssigned"] == item.isAssigned;
        }
      });

      item.childType = childType;
      item.childEntities = [];
      item.nvrEntities = [];
      item.cameraEntities = [];
      if (matchedSchedules.length > 0) {
        matchedSchedules.forEach((match: any) => {
          if (match['nvrID'] != 0) {
            let entityToInsert = this.getEntityName(match['nvrID'], 'nvr', cameraResourceDataSource, nvrResourceDataSource);
            if (entityToInsert !== null && item.nvrEntities.indexOf(entityToInsert) === -1) {
              item.nvrEntities.push(entityToInsert);
            }
          }
        });
        matchedSchedules.forEach((match: any) => {
          if (match['cameraID'] != 0) {
            let entityToInsert = this.getEntityName(match['cameraID'], 'camera', cameraResourceDataSource, nvrResourceDataSource);
            if (entityToInsert !== null && item.cameraEntities.indexOf(entityToInsert) === -1) {
              item.cameraEntities.push(entityToInsert);
            }
          }
        });
        matchedSchedules.forEach((match: any) => {
          let entityToInsert = this.getEntityName(match[idName], childType, cameraResourceDataSource, nvrResourceDataSource);
          if (entityToInsert !== null && !item.childEntities.includes(entityToInsert)) {
            item.childEntities.push(entityToInsert);
          }
        });
      }
    });

    // Adjust schedules for the selected week.
    let adjustedScheds: CalendarSchedule[] = [];
    uniqueSched.forEach((item: CalendarSchedule) => {
      // Set updated date
      item.startTime = this.getAdjustedDate(item.startTime, item.recurrenceRule);
      item.endTime = this.getAdjustedDate(item.endTime, item.recurrenceRule);

      // Legacy EJS week-splitting truncates recurring schedules to day boundaries,
      // which shows duplicate bars and incorrect times in the FullCalendar Site Details view.
      adjustedScheds.push(item);
    });

    const hasSameChildEntities = (left: CalendarSchedule, right: CalendarSchedule) =>
      left.childEntities.length === right.childEntities.length &&
      left.childEntities.every((childValue) => right.childEntities.includes(childValue)) &&
      right.childEntities.every((childValue) => left.childEntities.includes(childValue));

    const isAdjacentSchedule = (left: CalendarSchedule, right: CalendarSchedule) => {
      if (left.id === right.id && hasSameChildEntities(left, right) && left.subject === right.subject) {
        return (Math.abs(left.endTime.getTime() - right.startTime.getTime()) <= 1000)
          || (Math.abs(right.endTime.getTime() - left.startTime.getTime()) <= 1000);
      }

      if ((left.subject === 'Monitored Override' || left.subject === 'Non-Monitored Override') && left.subject === right.subject) {
        return left.recurrenceRule === right.recurrenceRule
          && left.startTime !== right.startTime
          && left.endTime !== right.endTime
          && (
            (Math.abs(left.startTime.getTime() - right.endTime.getTime()) <= 1000)
            || (Math.abs(left.endTime.getTime() - right.startTime.getTime()) <= 1000)
          );
      }

      return false;
    };

    // Group adjacent schedules once so a contiguous chain produces one schedule.
    let adjustedSchedGroups: CalendarSchedule[][] = [];
    let visited = new Set<number>();

    adjustedScheds.forEach((item, i) => {
      if (visited.has(i)) {
        return;
      }

      let group: CalendarSchedule[] = [];
      let queue: number[] = [i];
      visited.add(i);

      while (queue.length > 0) {
        let currentIndex = queue.shift()!;
        let currentItem = adjustedScheds[currentIndex];

        group.push(currentItem);

        adjustedScheds.forEach((candidate, candidateIndex) => {
          if (visited.has(candidateIndex)) {
            return;
          }

          if (isAdjacentSchedule(currentItem, candidate)) {
            visited.add(candidateIndex);
            queue.push(candidateIndex);
          }
        });
      }

      group.sort((a, b) => a.startTime.getTime() > b.startTime.getTime() ? 1 : -1);
      adjustedSchedGroups.push(group);
    });

    // Iterate through each group and consolidate each group into one rolling schedule
    let schedules: CalendarSchedule[] = [];
    adjustedSchedGroups.forEach((group) => {
      let schedule = Object.assign(new CalendarSchedule(), group[0]);

      // Get earliest start time
      let startTime = group.map(sched => sched.startTime).sort((a, b) => a.getTime() > b.getTime() ? 1 : -1)[0];
      // Get latest end time
      let endTime = group.map(sched => sched.endTime).sort((a, b) => a.getTime() < b.getTime() ? 1 : -1)[0];

      schedule.startTime = startTime;
      schedule.endTime = endTime;

      schedule.startTime = this.setTimelineSchedule(schedule.startTime, schedule.olson, schedule.isDSTIgnored);
      schedule.endTime = this.setTimelineSchedule(schedule.endTime, schedule.olson, schedule.isDSTIgnored);

      // Populate timestrings
      this.transformTimeStrings(schedule, false);

      schedules.push(schedule);
    });

    return schedules;
  }

  getFilteredUniqueSchedules(filter: any, calendarSchedules: Array<Object>, cameraResourceDataSource: DeviceDto[], nvrResourceDataSource: DeviceDto[], siteResourceDataSource: Site[]): CalendarSchedule[] {
    let filteredSched = calendarSchedules.filter(function (schedule: any) {
      for (let key in filter) {
        if (schedule[key] === undefined || schedule[key] != filter[key]) {
          return false;
        }
      }
      return true;
    });

    //Get unique schedules
    let uniqueSched: Array<CalendarSchedule> = [];

    filteredSched.forEach(function (item: any) {
      let isPresent = uniqueSched.filter(function (elem: any) {
        if (item["subject"] != 'Monitored Override' && item["subject"] != 'Non-Monitored Override') {
          // Include `id` so distinct schedule templates with identical time patterns are never merged
          return elem.id == item["id"] && elem.subject == item["subject"] && elem.recurrenceRule == item["recurrenceRule"] &&
            elem.startTime.toISOString() == item["startTime"].toISOString() &&
            elem.endTime.toISOString() == item["endTime"].toISOString() && elem.isAssigned == item["isAssigned"];
        }
        else {
          return elem.subject == item["subject"] &&
            elem.startTime.toISOString() == item["startTime"].toISOString() &&
            elem.endTime.toISOString() == item["endTime"].toISOString() && elem.isAssigned == item["isAssigned"];
        }
      });
      if (isPresent.length === 0) {
        let insertItem = Object.assign(new CalendarSchedule(), item);
        uniqueSched.push(insertItem);
      }
    });

    // List down child entities
    let childType = '';
    let idName = '';
    if (filter['cameraID'] !== undefined) {
      childType = 'camera';
      idName = 'cameraID';
    }
    else if (filter['nvrID'] !== undefined) {
      childType = 'camera';
      idName = 'cameraID';
    }
    else if (filter['siteID'] !== undefined) {
      childType = 'nvr';
      idName = 'nvrID';
    }
    else if (filter['customerID'] !== undefined) {
      childType = 'site';
      idName = 'siteID';
    }
    uniqueSched.forEach(item => {
      let isMatch = filteredSched.filter(function (elem: any) {
        if (item.subject != 'Monitored Override' && item.subject != 'Non-Monitored Override') {
          // Include `id` so only records belonging to this exact template contribute child sites/NVRs/cameras
          return elem["id"] == item.id && elem["subject"] == item.subject && elem["recurrenceRule"] == item.recurrenceRule &&
            elem["startTime"].toISOString() == item.startTime.toISOString() &&
            elem["endTime"].toISOString() == item.endTime.toISOString() && elem["isAssigned"] == item.isAssigned;
        }
        else {
          return elem["subject"] == item.subject &&
            elem["startTime"].toISOString() == item.startTime.toISOString() &&
            elem["endTime"].toISOString() == item.endTime.toISOString() && elem["isAssigned"] == item.isAssigned;
        }
      });

      item.childType = childType;
      item.childEntities = [];
      if (isMatch.length > 0) {
        isMatch.forEach((match: any) => {
          let entityToInsert = this.getEntityName(match[idName], childType, cameraResourceDataSource, nvrResourceDataSource, siteResourceDataSource);
          if (entityToInsert !== null && !item.childEntities.includes(entityToInsert)) {
            item.childEntities.push(entityToInsert);
          }
        });
      }
    });

    uniqueSched.forEach((schedule: CalendarSchedule) => {
      schedule.startTime = this.getAdjustedDate(schedule.startTime, schedule.recurrenceRule);
      schedule.endTime = this.getAdjustedDate(schedule.endTime, schedule.recurrenceRule);
      this.transformTimeStrings(schedule, false);
    });

    // Filter out schedules with empty childEntities
    uniqueSched = uniqueSched.filter((schedule: CalendarSchedule) => schedule.childEntities && schedule.childEntities.length > 0);

    return uniqueSched;
  }

  /**
   * This function gets the adjusted date based on the recurrence rule
  */
  getAdjustedDate(input: Date, recurrenceRule: string, olson?: string): Date {
    let output = DateTime.fromJSDate(input);

    // Set weekday based on recurrenceRule
    switch (recurrenceRule) {
      case "FREQ=WEEKLY;BYDAY=SU;INTERVAL=1;": output = output.set({ weekday: 7 }); break;
      case "FREQ=WEEKLY;BYDAY=MO;INTERVAL=1;": output = output.set({ weekday: 1 }); break;
      case "FREQ=WEEKLY;BYDAY=TU;INTERVAL=1;": output = output.set({ weekday: 2 }); break;
      case "FREQ=WEEKLY;BYDAY=WE;INTERVAL=1;": output = output.set({ weekday: 3 }); break;
      case "FREQ=WEEKLY;BYDAY=TH;INTERVAL=1;": output = output.set({ weekday: 4 }); break;
      case "FREQ=WEEKLY;BYDAY=FR;INTERVAL=1;": output = output.set({ weekday: 5 }); break;
      case "FREQ=WEEKLY;BYDAY=SA;INTERVAL=1;": output = output.set({ weekday: 6 }); break;
      default: break;
    }

    // Apply timezone if provided
    if (olson) {
      output = output.setZone(olson, { keepLocalTime: true });
    }

    return output.toJSDate();
  }

  /**
   * This function gets all the entity names assigned to the schedule
  */
  getEntityName(id: number, type: string, cameraResourceDataSource: DeviceDto[], nvrResourceDataSource: DeviceDto[], siteResourceDataSource?: Site[]): string | null {
    let index = 0;
    if (type === 'camera') {
      index = cameraResourceDataSource.findIndex(camera => camera.id === id);
      if (index >= 0) {
        return cameraResourceDataSource[index].name!;
      }
    }
    else if (type === 'nvr') {
      index = nvrResourceDataSource.findIndex(nvr => nvr.id === id);
      if (index >= 0) {
        return nvrResourceDataSource[index].name!;
      }
    }
    else if (type === 'site') {
      index = siteResourceDataSource!.findIndex(site => site.id === id);
      if (index >= 0) {
        return siteResourceDataSource![index].name;
      }
    }
    return null;
  }

  /**
   * This function sets the timeline schedule based on the source timezone
  */
  setTimelineSchedule(value: Date, sourceTimezone: string, ignoreDST: boolean): Date {
    if (sourceTimezone !== undefined) {
      let validTimezoneNames = moment.tz.names();
      if (validTimezoneNames.findIndex(timezone => timezone === sourceTimezone) >= 0) {
        if (ignoreDST === false) {
          // Use Timezone
          let luxonTime = DateTime.fromJSDate(value).setZone(sourceTimezone, { keepLocalTime: true });
          let date = luxonTime.toJSDate();
          return date;
        }
        else {
          // Use Non-DST Timezone
          let luxonTime = DateTime.fromJSDate(value).setZone(sourceTimezone, { keepLocalTime: false }).plus({ hour: 1 })
          let date = luxonTime.toJSDate();
          return date;
        }
      }
    }
    return value;
  }

  getAdjacent(uniqueSched: CalendarSchedule[], item: CalendarSchedule, tempList: number[], i: number): CalendarSchedule[] {
    let group: Array<any> = [];
    if (tempList.findIndex(index => index === i) === -1) {
      group = uniqueSched.filter(function (elem, j) {
        let isAdjacent = false;
        if (tempList.findIndex(index => index === j) === -1) {
          if (elem.id === item.id && elem.childEntities.length === item.childEntities.length && elem.subject === item.subject &&
            (elem.childEntities.every(function (childValue, childIndex) { return childValue === item.childEntities[childIndex] }))) {
            isAdjacent = (Math.abs(item.endTime.getTime() - elem.startTime.getTime()) <= 1000) || (Math.abs(elem.endTime.getTime() - item.startTime.getTime()) <= 1000);
          }
          else if ((elem.subject === 'Monitored Override' || elem.subject === 'Non-Monitored Override') && elem.subject === item.subject) {
            isAdjacent = elem.recurrenceRule === item.recurrenceRule &&
              elem.startTime !== item.startTime && elem.endTime !== item.endTime &&
              (Math.abs(elem.startTime.getTime() - item.endTime.getTime()) <= 1000) || (Math.abs(elem.endTime.getTime() - item.startTime.getTime()) <= 1000);
          }
        }
        return isAdjacent;
      });

      tempList.push(i);
      group.push(item);
      if (group.length > 0) {
        group.forEach((item) => {
          let tempIndex = uniqueSched.findIndex(value => JSON.stringify(value) === JSON.stringify(item));
          group = group.concat(this.getAdjacent(uniqueSched, item, tempList, tempIndex));
        });
      }
    }

    return group;
  }

  /**
   * This function sets the time strings for our tooltips
  */
  transformTimeStrings(adjustedSched: CalendarSchedule, calTime: boolean) {
    adjustedSched.localTimeString = this.getTimeString(adjustedSched.startTime, adjustedSched.endTime, adjustedSched.olson, adjustedSched.isDSTIgnored, calTime, '', '');
    adjustedSched.dallasTimeString = this.getTimeString(adjustedSched.startTime, adjustedSched.endTime, adjustedSched.olson, adjustedSched.isDSTIgnored, calTime, 'US/Central', 'GMT-6');
    adjustedSched.torontoTimeString = this.getTimeString(adjustedSched.startTime, adjustedSched.endTime, adjustedSched.olson, adjustedSched.isDSTIgnored, calTime, 'America/Toronto', 'GMT-5');
    adjustedSched.manilaTimeString = this.getTimeString(adjustedSched.startTime, adjustedSched.endTime, adjustedSched.olson, adjustedSched.isDSTIgnored, calTime, 'Asia/Manila', 'GMT+8');
  }

  /**
   * This function gets the site time for our tooltips
  */
  getTimeString(startValue: Date, endValue: Date, sourceTimezone: string, ignoreDST: boolean, calTime: boolean, tzFormat: string, utc: string): string {
    if (sourceTimezone !== undefined) {
      let validTimezoneNames = moment.tz.names();
      if (validTimezoneNames.findIndex(timezone => timezone === sourceTimezone) >= 0) {
        let luxonStartTime: DateTime;
        let luxonendTime: DateTime;

        if (ignoreDST === false) {
          // Use Timezone
          luxonStartTime = DateTime.fromJSDate(startValue).setZone(sourceTimezone, { keepCalendarTime: calTime });
          luxonendTime = DateTime.fromJSDate(endValue).setZone(sourceTimezone, { keepCalendarTime: calTime });
        } else {
          // Use UTC Offset (DST is ignored)
          luxonStartTime = DateTime.fromJSDate(startValue).setZone(sourceTimezone, { keepCalendarTime: calTime }).plus({ hour: 1 });
          luxonendTime = DateTime.fromJSDate(endValue).setZone(sourceTimezone, { keepCalendarTime: calTime }).plus({ hour: 1 });
        }

        // Format both based on time zones
        if (tzFormat !== '') {
          return luxonStartTime.setZone(tzFormat).toFormat('t') + ' - ' + luxonendTime.setZone(tzFormat).toFormat('t');
        } else {
          return luxonStartTime.toFormat('t') + ' - ' + luxonendTime.toFormat('t');
        }
      }
    }
    return new DatePipe('en-Us').transform(startValue, 'h:mm a', utc) + ' - ' + new DatePipe('en-Us').transform(endValue, 'h:mm a', utc);
  }
  //#endregion Scheduler

  getDeviceIDsFromParent(calendarData: CalendarSchedule, calendarSchedules: CalendarSchedule[]): number[] {
    let filteredSched: CalendarSchedule[] = [];
    if (calendarData.childType === 'site') {
      filteredSched = calendarSchedules.filter(schedule => schedule.customerID == calendarData.customerID
        && schedule.id == calendarData.id
        && schedule.isAssigned == calendarData.isAssigned);
    }
    else if (calendarData.childType === 'nvr') {
      filteredSched = calendarSchedules.filter(schedule => schedule.customerID == calendarData.customerID
        && schedule.siteID == calendarData.siteID
        && schedule.id == calendarData.id
        && schedule.isAssigned == calendarData.isAssigned);
    }
    else if (calendarData.childType === 'camera') {
      filteredSched = calendarSchedules.filter(schedule => schedule.customerID == calendarData.customerID
        && schedule.siteID == calendarData.siteID
        && schedule.nvrID == calendarData.nvrID
        && schedule.id == calendarData.id
        && schedule.isAssigned == calendarData.isAssigned);
    }
    else {
      filteredSched = calendarSchedules.filter(schedule => schedule.customerID == calendarData.customerID
        && schedule.siteID == calendarData.siteID
        && schedule.nvrID == calendarData.nvrID
        && schedule.cameraID == calendarData.cameraID
        && schedule.id == calendarData.id
        && schedule.isAssigned == calendarData.isAssigned);
    }

    let NVRIDs = filteredSched.filter(schedule => schedule.cameraID == 0).map(item => item.nvrID);
    let CameraIDs = filteredSched.filter(schedule => schedule.cameraID != 0).map(item => item.cameraID);

    let devicedIDs = NVRIDs.concat(CameraIDs);
    return [... new Set(devicedIDs)];
  }

  getScheduleExceptionsFromParent(calendarData: CalendarSchedule, calendarSchedules: CalendarSchedule[]): CalendarSchedule[] {
    let filteredSched: CalendarSchedule[] = [];
    if (calendarData.childType === 'site') {
      filteredSched = calendarSchedules.filter(schedule => schedule.subject === calendarData.subject
        && schedule.customerID == calendarData.customerID
        && schedule.startTime.toISOString() == calendarData.startTime.toISOString()
        && schedule.endTime.toISOString() == calendarData.endTime.toISOString()
        && schedule.isHoliday == calendarData.isHoliday
        && schedule.notes == calendarData.notes);
    }
    else if (calendarData.childType === 'nvr') {
      filteredSched = calendarSchedules.filter(schedule => schedule.subject === calendarData.subject
        && schedule.customerID == calendarData.customerID
        && schedule.siteID == calendarData.siteID
        && schedule.startTime.toISOString() == calendarData.startTime.toISOString()
        && schedule.endTime.toISOString() == calendarData.endTime.toISOString()
        && schedule.isHoliday == calendarData.isHoliday
        && schedule.notes == calendarData.notes);
    }
    else if (calendarData.childType === 'camera') {
      filteredSched = calendarSchedules.filter(schedule => schedule.subject === calendarData.subject
        && schedule.customerID == calendarData.customerID
        && schedule.siteID == calendarData.siteID
        && schedule.nvrID == calendarData.nvrID
        && schedule.startTime.toISOString() == calendarData.startTime.toISOString()
        && schedule.endTime.toISOString() == calendarData.endTime.toISOString()
        && schedule.isHoliday == calendarData.isHoliday
        && schedule.notes == calendarData.notes);
    }
    else {
      filteredSched.push(calendarData);
    }

    return filteredSched;
  }

  getUtcDate(input: Date): Date {
    let userTimezoneOffset = input.getTimezoneOffset() * 60000;
    let outputDate = new Date(input.getTime() - userTimezoneOffset);

    if (input.getHours() === 23 && input.getMinutes() === 59 && input.getSeconds() === 0) {
      // Add seconds to max time per day
      outputDate = new Date(outputDate.getTime() + 59000);
    }
    return outputDate;
  }

}